import React from 'react';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';

import ContactBlock from '../../components/contact-block';
import PrimaryButton from '../../components/buttons/primary-button';
import InfoPanel from './components/info-panel';

import DottedLineImg from './images/dotted-line.png';
import Img1 from './images/hero-img.png';
import Img2 from './images/img-clinics.png';
import Img3 from './images/collaborate-img.png';
import Img4 from './images/userfriendly-img.png';
import Img5 from './images/training-img.png';
import Colors from '../../styles/colors';

const HeaderWrapper = styled('div')(({ theme }) => ({
    backgroundImage: `url(${DottedLineImg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 125,
    marginLeft: -40,
    height: 140,
    [theme.breakpoints.down('desktop')]: {
        marginLeft: 0,
        height: 'auto',
        backgroundImage: 'none',
    },
}));

const HeaderText = styled(Typography)(({ theme }) => ({
    marginLeft: 40,
    paddingTop: 20,
    [theme.breakpoints.down('desktop')]: {
        marginLeft: 0,
        paddingTop: 0,
        lineHeight: 'normal',
    },
}));

const QuoteText = styled(Typography)(() => ({
    color: Colors.darkGray,
    fontSize: '1.6rem',
    lineHeight: '25px',
}));

const Home = () => {
    const navigate = useNavigate();

    const muiTheme = useTheme();
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('desktop'), { noSsr: true });
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tablet'), { noSsr: true });

    return (
        <>
            <Container
                sx={{
                    backgroundColor: Colors.darkPurple,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'hidden',
                }}
                component="header"
            >
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.desktop, pb: { tablet: 15, mobile: 5 }, width: '100%' }}>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        sx={{ pt: { tablet: 14, mobile: 5 } }}
                    >
                        <Grid item mobile={12} tablet={6}>
                            <Typography
                                variant="h1"
                                style={{
                                    color: 'white',
                                    paddingBottom: 15,
                                }}
                            >
                                Pro Bono Management and Collaboration
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'white',
                                    paddingBottom: { mobile: 8, tablet: 15 },
                                    lineHeight: { mobile: 'normal', desktop: 'inherit' },
                                }}
                            >
                                Increase your impact with streamlined pro bono management and multi-organizational collaboration on pro bono matters
                            </Typography>
                            <PrimaryButton onClick={() => navigate('/demo')}>
                                Request Demo
                            </PrimaryButton>
                        </Grid>
                        <Grid item mobile={12} tablet={6} sx={{ display: 'flex', justifyContent: 'end', maxWidth: 'inherit' }}>
                            <img src={Img1} alt="" style={{ maxHeight: 520, maxWidth: isMobile ? 'inherit' : '100%' }} />
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Container
                sx={{
                    backgroundColor: '#FFFFFF',
                    pt: { tablet: 8, mobile: 4 },
                    pb: { tablet: 8, mobile: 4 },
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        margin: '0 auto',
                        pt: { mobile: 3, tablet: 6 },
                    }}
                >
                    <InfoPanel />
                </Box>
            </Container>
            <Container
                sx={{
                    pt: { tablet: 8, mobile: 4 },
                    pb: { tablet: 8, mobile: 4 },
                    backgroundColor: '#FAFAFA',
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        margin: '0 auto',
                    }}
                >
                    <Grid container spacing={{ mobile: 3, tablet: 12 }} sx={{ alignItems: 'center' }}>
                        <Grid item tablet={6} mobile={12}>
                            <HeaderWrapper>
                                <HeaderText variant="h2">
                                    Easy Management of Your Pro Bono Matters
                                </HeaderText>
                            </HeaderWrapper>

                            <Typography variant="body2" pt={2} sx={{ lineHeight: { mobile: 'normal', desktop: 'inherit' } }}>
                                Add new pro bono matters, manage attorneys, assign matters, and create training materials all in one easy to use administrative dashboard.
                            </Typography>
                        </Grid>
                        <Grid item tablet={6} mobile={12}>
                            <img src={Img2} alt="" style={{ maxHeight: 520 }} />
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Container
                sx={{
                    pt: { tablet: 8, mobile: 4 },
                    pb: { tablet: 8, mobile: 4 },
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        margin: '0 auto',
                    }}
                >
                    <Grid container spacing={{ mobile: 0, tablet: 12 }} sx={{ alignItems: 'center' }}>
                        <Grid item tablet={6} mobile={12} order={{ mobile: 2, tablet: 1 }}>
                            <img src={Img3} alt="" style={{ maxHeight: isTablet ? 450 : 520 }} />
                        </Grid>
                        <Grid item tablet={6} mobile={12} order={{ mobile: 1, tablet: 2 }}>
                            <HeaderWrapper>
                                <HeaderText variant="h2">
                                    Collaborate Across Organizations
                                </HeaderText>
                            </HeaderWrapper>

                            <Typography variant="body2" pt={2} sx={{ lineHeight: { mobile: 'normal', desktop: 'inherit' } }}>
                                Lawyers from law firms, corporate legal departments and legal services organizations can work together seamlessly to ensure effective and efficient representation of pro bono clients.
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Container
                sx={{
                    pt: { tablet: 8, mobile: 4 },
                    pb: { tablet: 8, mobile: 4 },
                    backgroundColor: '#FAFAFA',
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        margin: '0 auto',
                    }}
                >
                    <Grid container spacing={{ mobile: 0, tablet: 12 }} sx={{ alignItems: 'center' }}>
                        <Grid item tablet={6} mobile={12}>
                            <HeaderWrapper>
                                <HeaderText variant="h2">
                                    Training and Recruiting<br />made easier
                                </HeaderText>
                            </HeaderWrapper>

                            <Typography variant="body2" pt={2} sx={{ lineHeight: { mobile: 'normal', desktop: 'inherit' } }}>
                                Make training and recruiting team members to help pro bono clients easier and more engaging. Virtual Law Clinic allows you to create training materials and add forms and templates to make it easy for all lawyers to dedicate time to pro bono.
                            </Typography>
                        </Grid>
                        <Grid item tablet={6} mobile={12}>
                            <img src={Img5} alt="" style={{ maxHeight: 520 }} />
                        </Grid>
                    </Grid>
                </Box>
            </Container>
            <Container
                sx={{
                    pt: { tablet: 8, mobile: 4 },
                    pb: { tablet: 12, mobile: 8 },
                    overflowX: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        margin: '0 auto',
                    }}
                >
                    <Grid container spacing={{ mobile: 6, tablet: 12 }} sx={{ alignItems: 'center' }}>
                        <Grid item tablet={6} mobile={12} order={{ mobile: 2, tablet: 1 }}>
                            <img src={Img4} alt="" style={{ maxHeight: 520 }} />
                        </Grid>
                        <Grid item tablet={6} mobile={12} order={{ mobile: 1, tablet: 2 }}>
                            <HeaderWrapper>
                                <HeaderText variant="h2">
                                    User Friendly Document Uploading
                                </HeaderText>
                            </HeaderWrapper>

                            <Typography variant="body2" pt={2} sx={{ lineHeight: { mobile: 'normal', desktop: 'inherit' } }}>
                                VLC makes it easy for you to request and receive documents from pro bono clients. With one click, your client will be prompted to take photographs of important documents and easily upload them to your dashboard without having to create an account.
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            <Container
                sx={{
                    backgroundColor: Colors.disabledBtnBackground,
                }}
            >
                <Box
                    sx={{
                        maxWidth: (theme) => theme.breakpoints.values.desktop,
                        pb: { tablet: 8, mobile: 0 },
                        width: '100%',
                        margin: '0 auto',
                    }}
                >
                    <Grid
                        container
                        direction="row"
                        justifyContent="center"
                        sx={{
                            pt: { tablet: 8, mobile: 4 },
                            spacing: { tablet: 10, mobile: 0 },
                        }}
                        spacing={10}
                    >
                        <Grid item mobile={12} style={{ textAlign: 'center' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    pb: { mobile: 0, tablet: 3 },
                                }}
                            >
                                Clients are loving Virtual Law Clinic
                            </Typography>
                        </Grid>
                        <Grid item mobile={12} tablet={6}>
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    width: '100%',
                                    padding: 30,
                                    borderRadius: '11px 11px 0 0',
                                }}
                            >
                                <QuoteText>
                                    This product has significantly impacted the way we deliver pro bono services. It has made our pro bono team more efficient and allows us to better track the work that the volunteer lawyers are doing.  We&apos;re proud to bring our clients into the platform to collaborate on pro bono matters and build stronger relationships.
                                </QuoteText>
                            </div>
                            <div
                                style={{
                                    backgroundColor: Colors.lightBeige,
                                    width: '100%',
                                    padding: 30,
                                    borderRadius: '0 0 11px 11px',
                                }}
                            >
                                <Typography variant="body1">
                                    <b>
                                        Harlene Katzman
                                    </b>
                                </Typography>
                                <QuoteText>
                                    Pro Bono Counsel & Director
                                </QuoteText>
                                <QuoteText>
                                    Simpson Thacher & Bartlett LLP
                                </QuoteText>
                            </div>
                        </Grid>
                        <Grid item mobile={12} tablet={6}>
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    width: '100%',
                                    padding: 30,
                                    borderRadius: '11px 11px 0 0',
                                }}
                            >
                                <QuoteText>
                                    The platform streamlined our coordination process by providing a central base to upload client files and forms that could be accessed and reviewed by volunteers as soon as they were uploaded. During clinics, our legal staff can see the real time case progress as volunteers completed forms, allowing for easy review. The comment section allows for collaboration and communication between volunteers and our legal staff. All in an easy-to-use platform without the worry of having to learn a complicated new system.
                                </QuoteText>
                            </div>
                            <div
                                style={{
                                    backgroundColor: Colors.lightBeige,
                                    width: '100%',
                                    padding: 30,
                                    borderRadius: '0 0 11px 11px',
                                }}
                            >
                                <Typography variant="body1">
                                    <b>
                                        Jenifer Guzman
                                    </b>
                                </Typography>
                                <QuoteText>
                                    Community Advocate, Immigration Project
                                </QuoteText>
                                <QuoteText>
                                    Volunteers of Legal Service
                                </QuoteText>
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            <ContactBlock />
        </>
    );
};

export default Home;
